import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { cn } from "@/utils/cn";

interface TableSearchProps {
	onSearch: (term: string, matchIndex: number) => void;
	matchCount: number;
	placeholder?: string;
}

export interface TableSearchRef {
	focus: () => void;
}

const TableSearch = forwardRef<TableSearchRef, TableSearchProps>(
	({ onSearch, matchCount, placeholder = "Filtrera tidrapporter" }, ref) => {
		const [searchTerm, setSearchTerm] = useState("");
		const [isFocused, setIsFocused] = useState(false);
		const inputRef = useRef<HTMLInputElement>(null);
		const lastSearchTermRef = useRef<string>("");

		// Expose focus method to parent
		useImperativeHandle(ref, () => ({
			focus: () => {
				inputRef.current?.focus();
			},
		}));

		useEffect(() => {
			// Only trigger search if the term actually changed
			if (searchTerm !== lastSearchTermRef.current) {
				lastSearchTermRef.current = searchTerm;
				if (searchTerm) {
					onSearch(searchTerm, 0);
				} else {
					// Clear search when term is empty
					onSearch("", 0);
				}
			}
		}, [searchTerm, onSearch]);

		const handleKeyDown = (e: React.KeyboardEvent) => {
			// No special key handling needed
		};

		return (
			<div
				className={cn(
					"bg-white border rounded-[2px] h-[34px] px-2 flex items-center gap-2 flex-1 transition-colors",
					isFocused ? "border-action ring-1 ring-action" : "border-[#DBDBDB]"
				)}
			>
				<input
					ref={inputRef}
					type="text"
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					onKeyDown={handleKeyDown}
					onFocus={() => setIsFocused(true)}
					onBlur={() => setIsFocused(false)}
					placeholder={placeholder}
					className="flex-1 px-2 py-1 border-0 focus:outline-none h-full min-w-[120px]"
				/>

				{/* <div className="h-full w-[1px] bg-gray-300" /> */}

				{searchTerm && matchCount > 0 && (
					<div className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
						<span>
							{matchCount} {matchCount > 1 ? "träffar" : "träff"}
						</span>
					</div>
				)}
			</div>
		);
	}
);

export default TableSearch;
